import { z } from 'zod';
import { moneyMinorStringSchema } from './money';

export type CustomerIdentityRevocationScope =
  'target_identity' | 'authenticated_session_generation';

export type CustomerChannelAutomationStatus = 'active' | 'opted_out';

export const assertChannelAutomationChangeAllowed = (
  status: CustomerChannelAutomationStatus
): void => {
  if (status === 'opted_out') {
    throw new Error(
      'Channel automation cannot change after opt-out without new explicit opt-in.'
    );
  }
};

export interface ConversationContinuityState {
  version: bigint;
  leaseToken: string | null;
  leaseExpiresAt: string | null;
}

export const continuityAfterIdentityTransition = (
  current: ConversationContinuityState
): ConversationContinuityState => {
  if (current.version < 0n) {
    throw new Error('Conversation version cannot be negative.');
  }
  if ((current.leaseToken === null) !== (current.leaseExpiresAt === null)) {
    throw new Error(
      'Conversation lease token and expiry must change together.'
    );
  }
  return {
    version: current.version + 1n,
    leaseToken: null,
    leaseExpiresAt: null,
  };
};

export type WebDeliveryDecision =
  | { kind: 'already_delivered'; outboundMessageId: string }
  | { kind: 'deliver' }
  | { kind: 'reject'; reason: 'inactive_web_session' };

export const decideWebDelivery = (input: {
  existingOutboundMessageId: string | null;
  activeSessionExpiresAt: string | null;
  now: Date;
}): WebDeliveryDecision => {
  if (Number.isNaN(input.now.getTime())) {
    throw new Error('Web delivery evaluation time must be valid.');
  }
  if (input.existingOutboundMessageId) {
    return {
      kind: 'already_delivered',
      outboundMessageId: input.existingOutboundMessageId,
    };
  }
  if (!input.activeSessionExpiresAt) {
    return { kind: 'reject', reason: 'inactive_web_session' };
  }
  const sessionExpiry = new Date(input.activeSessionExpiresAt);
  if (Number.isNaN(sessionExpiry.getTime())) {
    throw new Error('Web session expiry must be a valid timestamp.');
  }
  return sessionExpiry.getTime() > input.now.getTime()
    ? { kind: 'deliver' }
    : { kind: 'reject', reason: 'inactive_web_session' };
};

export const needValueSchema = z.union([
  z.string().trim().min(1).max(500),
  z.number().finite(),
  z.boolean(),
]);

export const needConstraintSchema = z
  .object({
    key: z.string().trim().min(1).max(100),
    value: needValueSchema,
  })
  .strict()
  .superRefine((constraint, context) => {
    if (
      /(?:price|budget|amount|cost|precio|presupuesto)/iu.test(
        constraint.key
      ) &&
      typeof constraint.value === 'number'
    ) {
      context.addIssue({
        code: 'custom',
        message:
          'Monetary needs require an exact money_minor range, not a number value.',
      });
    }
  });

export type NeedConstraint = z.infer<typeof needConstraintSchema>;

const quantityNeedRangeSchema = z
  .object({
    key: z.string().trim().min(1).max(100),
    kind: z.literal('quantity'),
    minimum: z.number().finite().nullable(),
    maximum: z.number().finite().nullable(),
  })
  .strict()
  .superRefine((range, context) => {
    if (range.minimum === null && range.maximum === null) {
      context.addIssue({
        code: 'custom',
        message: 'A need range requires a minimum or maximum.',
      });
    }
    if (
      range.minimum !== null &&
      range.maximum !== null &&
      range.minimum > range.maximum
    ) {
      context.addIssue({
        code: 'custom',
        message: 'A need range minimum cannot exceed its maximum.',
      });
    }
  });

const moneyNeedRangeSchema = z
  .object({
    key: z.string().trim().min(1).max(100),
    kind: z.literal('money_minor'),
    currency: z.enum(['USD', 'PEN']),
    minimum: moneyMinorStringSchema.nullable(),
    maximum: moneyMinorStringSchema.nullable(),
  })
  .strict()
  .superRefine((range, context) => {
    if (range.minimum === null && range.maximum === null) {
      context.addIssue({
        code: 'custom',
        message: 'A money range requires a minimum or maximum.',
      });
    }
    if (
      range.minimum !== null &&
      range.maximum !== null &&
      BigInt(range.minimum) > BigInt(range.maximum)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'A money range minimum cannot exceed its maximum.',
      });
    }
  });

export const needRangeSchema = z.discriminatedUnion('kind', [
  quantityNeedRangeSchema,
  moneyNeedRangeSchema,
]);

export type NeedRange = z.infer<typeof needRangeSchema>;

export const alternativeNeedGroupSchema = z
  .object({
    key: z.string().trim().min(1).max(100),
    options: z.array(needConstraintSchema).min(2).max(20),
  })
  .strict()
  .superRefine((group, context) => {
    const optionKeys = new Set(group.options.map((option) => option.key));
    if (optionKeys.size !== 1 || !optionKeys.has(group.key)) {
      context.addIssue({
        code: 'custom',
        message: 'Alternative options must all use the group key.',
      });
    }
  });

export type AlternativeNeedGroup = z.infer<typeof alternativeNeedGroupSchema>;

export const customerNeedProfileSchema = z
  .object({
    required: z.array(needConstraintSchema).max(100),
    excluded: z.array(needConstraintSchema).max(100),
    ranges: z.array(needRangeSchema).max(100),
    alternatives: z.array(alternativeNeedGroupSchema).max(20),
  })
  .strict();

export type CustomerNeedProfile = z.infer<typeof customerNeedProfileSchema>;

export const EMPTY_CUSTOMER_NEED_PROFILE: CustomerNeedProfile = {
  required: [],
  excluded: [],
  ranges: [],
  alternatives: [],
};

export const customerNeedPatchSchema = z
  .object({
    setRequired: z.array(needConstraintSchema).max(100),
    setExcluded: z.array(needConstraintSchema).max(100),
    setRanges: z.array(needRangeSchema).max(100),
    removeKeys: z.array(z.string().trim().min(1).max(100)).max(100),
    replaceAlternatives: z.array(alternativeNeedGroupSchema).max(20).optional(),
  })
  .strict()
  .superRefine((patch, context) => {
    const changedKeys = new Set([
      ...patch.setRequired.map((constraint) => constraint.key),
      ...patch.setExcluded.map((constraint) => constraint.key),
      ...patch.setRanges.map((range) => range.key),
      ...(patch.replaceAlternatives ?? []).map((group) => group.key),
    ]);
    for (const removedKey of patch.removeKeys) {
      if (changedKeys.has(removedKey)) {
        context.addIssue({
          code: 'custom',
          message: `Need patch cannot remove and set ${removedKey} in one unordered update.`,
        });
      }
    }
  });

export type CustomerNeedPatch = z.infer<typeof customerNeedPatchSchema>;

const latestConstraintsByKey = (
  current: readonly NeedConstraint[],
  replacements: readonly NeedConstraint[],
  removedKeys: ReadonlySet<string>
): NeedConstraint[] => {
  const replacementKeys = new Set(
    replacements.map((replacement) => replacement.key)
  );
  return [
    ...current.filter(
      (constraint) =>
        !removedKeys.has(constraint.key) && !replacementKeys.has(constraint.key)
    ),
    ...replacements,
  ];
};

const latestRangesByKey = (
  current: readonly NeedRange[],
  replacements: readonly NeedRange[],
  removedKeys: ReadonlySet<string>
): NeedRange[] => {
  const byKey = new Map(
    current
      .filter((range) => !removedKeys.has(range.key))
      .map((range) => [range.key, range])
  );
  for (const replacement of replacements) {
    byKey.set(replacement.key, replacement);
  }
  return [...byKey.values()];
};

export const applyCustomerNeedPatch = (
  current: CustomerNeedProfile,
  rawPatch: CustomerNeedPatch
): CustomerNeedProfile => {
  const profile = customerNeedProfileSchema.parse(current);
  const patch = customerNeedPatchSchema.parse(rawPatch);
  const removedKeys = new Set(patch.removeKeys);
  return customerNeedProfileSchema.parse({
    required: latestConstraintsByKey(
      profile.required,
      patch.setRequired,
      removedKeys
    ),
    excluded: latestConstraintsByKey(
      profile.excluded,
      patch.setExcluded,
      removedKeys
    ),
    ranges: latestRangesByKey(profile.ranges, patch.setRanges, removedKeys),
    alternatives: patch.replaceAlternatives
      ? patch.replaceAlternatives
      : profile.alternatives.filter((group) => !removedKeys.has(group.key)),
  });
};

export type CommerceSearchPlan =
  | { kind: 'search'; needs: CustomerNeedProfile }
  | {
      kind: 'clarification_required';
      alternativeGroups: readonly AlternativeNeedGroup[];
    };

export const planCommerceSearch = (
  needs: CustomerNeedProfile,
  supportsAlternativeGroups: boolean
): CommerceSearchPlan => {
  const profile = customerNeedProfileSchema.parse(needs);
  if (!supportsAlternativeGroups && profile.alternatives.length > 0) {
    return {
      kind: 'clarification_required',
      alternativeGroups: profile.alternatives,
    };
  }
  return { kind: 'search', needs: profile };
};
